import { Link } from 'react-router-dom';
import { Target, Award, TrendingUp, Shield, Zap, BarChart3 } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-navy-dark py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4">
            <span className="text-cream">About </span>
            <span className="gradient-text-neon">Residual Vault</span>
          </h1>
          <p className="text-xl text-sky-blue">Built to make DeFi staking transparent and accessible</p>
        </div>

        <div className="card-premium mb-8">
          <h2 className="text-3xl font-bold text-cream mb-4">The Problem We Solve</h2>
          <p className="text-sky-blue text-lg mb-4">
            Crypto staking should be simple: find the best yield, understand the risks, and put your assets to work. But with hundreds of protocols across dozens of chains, each with different APYs, lock periods, and risk profiles, making informed decisions is overwhelming.
          </p>
          <p className="text-sky-blue text-lg">
            Residual Vault aggregates 156+ staking protocols into one platform with live APY data, risk ratings, and side-by-side comparison tools — so you can make smarter staking decisions in minutes, not hours.
          </p>
        </div>

        <div className="card-premium mb-8">
          <h2 className="text-3xl font-bold text-cream mb-4">Founded by a Builder</h2>
          <p className="text-sky-blue text-lg mb-4">
            Residual Vault was built by Mochtar Abukusumo — a digital asset investor and AI systems builder who got tired of manually tracking staking rates across dozens of platforms. What started as a personal tool became a full platform after realizing every crypto investor faces the same problem.
          </p>
          <p className="text-sky-blue text-lg mb-4">
            The entire platform — from the 31-agent AI content system to the live APY data pipeline — is built in-house. No third-party automation platforms, no shortcuts. Every piece of infrastructure is purpose-built for reliability and accuracy.
          </p>
          <p className="text-sky-blue text-lg">
            Residual Vault, LLC is a registered company operating from Albuquerque, New Mexico.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="card-premium">
            <div className="w-12 h-12 bg-gradient-cta rounded-lg flex items-center justify-center mb-4 shadow-neon">
              <Target className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-cream mb-2">Our Mission</h3>
            <p className="text-sky-blue">
              Give every crypto investor — from beginners to professionals — the data and tools to maximize staking returns while understanding the real risks involved.
            </p>
          </div>

          <div className="card-premium">
            <div className="w-12 h-12 bg-gradient-cta rounded-lg flex items-center justify-center mb-4 shadow-neon">
              <Award className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-cream mb-2">Our Values</h3>
            <p className="text-sky-blue">
              Transparency first. We show real data, real risks, and honest comparisons. No paid rankings, no hidden affiliates, no inflated numbers.
            </p>
          </div>
        </div>

        <div className="card-premium mb-8">
          <h2 className="text-3xl font-bold text-cream mb-6">What Makes Us Different</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <BarChart3 className="w-6 h-6 text-neon-red flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-cream mb-1">156+ Protocols Tracked</h3>
                <p className="text-sky-blue">The most comprehensive staking comparison database, covering all major chains and DeFi protocols with live APY data.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Shield className="w-6 h-6 text-neon-red flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-cream mb-1">Honest Risk Ratings</h3>
                <p className="text-sky-blue">Every protocol includes security assessments covering smart contract risk, depeg risk, and liquidity risk — not just the highest APY number.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Zap className="w-6 h-6 text-neon-red flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-cream mb-1">Free to Use</h3>
                <p className="text-sky-blue">Compare up to 10 protocols, use the calculator, and access all educational content completely free. No credit card required.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <TrendingUp className="w-6 h-6 text-neon-red flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-cream mb-1">Built on Real Data</h3>
                <p className="text-sky-blue">APY rates are fetched directly from protocol smart contracts and blockchain data — never estimated or averaged from third-party sources.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center card-premium">
          <h2 className="text-3xl font-bold text-cream mb-4">Ready to Get Started?</h2>
          <p className="text-sky-blue text-lg mb-6">
            Start comparing staking protocols and find the best yields for your portfolio
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="btn-neon inline-block">Create Free Account</Link>
            <Link to="/staking" className="bg-transparent border-2 border-sky-blue text-sky-blue font-semibold px-6 py-3 rounded-lg hover:bg-sky-blue/10 transition-all inline-block">Browse Protocols</Link>
          </div>
        </div>

      </div>
    </div>
  );
}
